import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import ScoreCardImage from "./ScoreCardImage";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: "🏸", label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const STATUS_CONFIG = {
  pending:       { label: "신청 중",      color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  accepted:      { label: "교류전 확정",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected:      { label: "상대 팀 거절", color: "#E8412A", bg: "#fff5f5", border: "#fecaca" },
  cancelled:     { label: "신청 취소됨",  color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  auto_rejected: { label: "자동 거절됨",  color: "#9ca3af", bg: "#f3f4f6", border: "#d1d5db" },
};

const WEEK_DAYS = ["일", "월", "화", "수", "목", "금", "토"];

function formatGameDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("ko-KR", { month: "long", day: "numeric" }) + ` (${WEEK_DAYS[d.getDay()]})`;
}

function buildLocation(post) {
  return [post?.location_city, post?.location_district, post?.location_name]
    .filter(Boolean).join(" ") || "-";
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "방금";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  return `${Math.floor(diff / 86400)}일 전`;
}

export default function MyMatchEvents({ onBack, onEventClick }) {
  const { user } = useAuth();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);

    // 1. 내가 속한 팀 id 목록 (팀장 + 팀원)
    const [{ data: ownTeams }, { data: memberships }] = await Promise.all([
      supabase.from("teams").select("id, team_name, logo_url, team_photo").eq("user_id", user.id),
      supabase.from("team_members").select("team_id").eq("user_id", user.id).eq("status", "accepted"),
    ]);

    const ownSet     = new Set((ownTeams || []).map(t => t.id));
    const extraIds   = (memberships || []).map(m => m.team_id).filter(id => !ownSet.has(id));
    const allTeamIds = [...ownSet, ...extraIds];

    if (allTeamIds.length === 0) { setItems([]); setLoading(false); return; }

    let allMyTeams = [...(ownTeams || [])];
    if (extraIds.length > 0) {
      const { data: memberTeams } = await supabase
        .from("teams").select("id, team_name, logo_url, team_photo").in("id", extraIds);
      allMyTeams = [...allMyTeams, ...(memberTeams || [])];
    }
    const myTeamSet = new Set(allTeamIds);

    // 2. applications 조회
    const [{ data: guestApps }, { data: hostApps }] = await Promise.all([
      supabase.from("applications")
        .select("id, host_team_id, guest_team_id, status, post_id, created_at")
        .in("guest_team_id", allTeamIds)
        .order("created_at", { ascending: false }),
      supabase.from("applications")
        .select("id, host_team_id, guest_team_id, status, post_id, created_at")
        .in("host_team_id", allTeamIds)
        .eq("status", "accepted")
        .order("created_at", { ascending: false }),
    ]);

    const seenIds = new Set();
    const allApps = [...(guestApps || []), ...(hostApps || [])]
      .filter(a => { if (seenIds.has(a.id)) return false; seenIds.add(a.id); return true; })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    if (allApps.length === 0) { setItems([]); setLoading(false); return; }

    // 3. post IDs + 상대팀 IDs 수집
    const postIds     = [...new Set(allApps.map(a => a.post_id).filter(Boolean))];
    const opponentIds = [...new Set(allApps.flatMap(a => {
      const isGuest = myTeamSet.has(a.guest_team_id);
      const oppId   = isGuest ? a.host_team_id : a.guest_team_id;
      return oppId ? [oppId] : [];
    }))].filter(id => !myTeamSet.has(id));

    const appIds = allApps.map(a => a.id);

    const [
      { data: posts },
      { data: opponentTeams },
      { data: results },
      { data: reactions },
    ] = await Promise.all([
      supabase.from("posts")
        .select("id, game_date, location_city, location_district, location_name, sports_type, team_name")
        .in("id", postIds),
      opponentIds.length > 0
        ? supabase.from("teams").select("id, team_name, logo_url, team_photo").in("id", opponentIds)
        : Promise.resolve({ data: [] }),
      supabase.from("match_results")
        .select("match_id, host_score, guest_score, result_status")
        .in("match_id", appIds)
        .eq("result_status", "confirmed"),
      supabase.from("match_reactions")
        .select("match_id, user_id, type")
        .in("match_id", appIds),
    ]);

    const postMap    = Object.fromEntries((posts || []).map(p => [p.id, p]));
    const allTeamMap = Object.fromEntries([...allMyTeams, ...(opponentTeams || [])].map(t => [t.id, t]));
    const resultMap  = Object.fromEntries((results || []).map(r => [r.match_id, r]));

    const reactMap = {};
    for (const r of (reactions || [])) {
      if (!reactMap[r.match_id]) reactMap[r.match_id] = { likeCount: 0, commentCount: 0, myLike: false };
      if (r.type === "like") {
        reactMap[r.match_id].likeCount++;
        if (r.user_id === user.id) reactMap[r.match_id].myLike = true;
      } else if (r.type === "comment") {
        reactMap[r.match_id].commentCount++;
      }
    }

    setItems(allApps.map(a => {
      const isGuest    = myTeamSet.has(a.guest_team_id);
      const myTeamId   = isGuest ? a.guest_team_id : a.host_team_id;
      const opponentId = isGuest ? a.host_team_id  : a.guest_team_id;
      return {
        application: a,
        post:        postMap[a.post_id]       || null,
        myTeam:      allTeamMap[myTeamId]     || null,
        hostTeam:    allTeamMap[opponentId]   || null,
        isGuest,
        result:      resultMap[a.id]          || null,
        reactions:   reactMap[a.id]           || { likeCount: 0, commentCount: 0, myLike: false },
      };
    }));
    setLoading(false);
  }, [user?.id]); // eslint-disable-line

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#E8412A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          뒤로
        </button>
        <span style={s.headerTitle}>우리 팀 교류전</span>
        <div style={{ width: 52 }} />
      </div>

      <div style={s.body}>
        {loading ? (
          <div style={s.centered}>
            <div style={s.spinner} />
          </div>
        ) : items.length === 0 ? (
          <div style={s.centered}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏅</div>
            <div style={{ fontSize: 15, color: "#9ca3af", fontWeight: 600 }}>아직 신청한 교류전이 없어요</div>
            <div style={{ fontSize: 13, color: "#c4c9d4", marginTop: 6 }}>교류전 모집글에 신청해보세요!</div>
          </div>
        ) : (
          items.map(item => (
            <EventCard
              key={item.application.id}
              item={item}
              onEventClick={onEventClick}
              currentUserId={user?.id}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EventCard({ item, onEventClick, currentUserId }) {
  const { application, post, hostTeam, myTeam, isGuest, result, reactions } = item;
  const statusCfg = STATUS_CONFIG[application.status] || STATUS_CONFIG.pending;
  const sport     = SPORTS[post?.sports_type] || { icon: "🏅", label: post?.sports_type || "-" };
  const thumbSrc  = hostTeam?.logo_url || hostTeam?.team_photo || null;

  /* ── 이미지 저장 ── */
  const [showCardImage, setShowCardImage] = useState(false);

  /* ── 좋아요 상태 ── */
  const [liked,        setLiked]        = useState(reactions.myLike);
  const [likeCount,    setLikeCount]    = useState(reactions.likeCount);
  const [likeLoading,  setLikeLoading]  = useState(false);

  /* ── 댓글 상태 ── */
  const [commentCount,    setCommentCount]    = useState(reactions.commentCount);
  const [showComments,    setShowComments]    = useState(false);
  const [comments,        setComments]        = useState([]);
  const [commentsLoaded,  setCommentsLoaded]  = useState(false);
  const [commentInput,    setCommentInput]    = useState("");
  const [commentLoading,  setCommentLoading]  = useState(false);

  /* ── 스코어 계산 ── */
  const myScore  = result ? (isGuest ? result.guest_score : result.host_score) : null;
  const oppScore = result ? (isGuest ? result.host_score  : result.guest_score) : null;
  const myWins   = result && myScore > oppScore;
  const oppWins  = result && oppScore > myScore;
  const isDraw   = result && myScore === oppScore;

  /* ── 댓글 로드 ── */
  const loadComments = useCallback(async () => {
    const { data: rows } = await supabase.from("match_reactions")
      .select("id, user_id, content, created_at")
      .eq("match_id", application.id)
      .eq("type", "comment")
      .order("created_at", { ascending: true });
    if (!rows || rows.length === 0) { setComments([]); setCommentsLoaded(true); return; }
    const userIds = [...new Set(rows.map(r => r.user_id))];
    const { data: profiles } = await supabase.from("user_profiles")
      .select("id, nickname").in("id", userIds);
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    setComments(rows.map(r => ({ ...r, nickname: profileMap[r.user_id]?.nickname || "?" })));
    setCommentsLoaded(true);
  }, [application.id]); // eslint-disable-line

  /* ── 좋아요 토글 ── */
  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentUserId || likeLoading) return;
    setLikeLoading(true);
    if (liked) {
      await supabase.from("match_reactions")
        .delete()
        .eq("match_id", application.id)
        .eq("user_id", currentUserId)
        .eq("type", "like");
      setLiked(false);
      setLikeCount(c => c - 1);
    } else {
      await supabase.from("match_reactions")
        .insert({ match_id: application.id, user_id: currentUserId, type: "like" });
      setLiked(true);
      setLikeCount(c => c + 1);
    }
    setLikeLoading(false);
  };

  /* ── 댓글 토글 ── */
  const handleToggleComments = async (e) => {
    e.stopPropagation();
    if (!showComments && !commentsLoaded) await loadComments();
    setShowComments(v => !v);
  };

  /* ── 댓글 등록 ── */
  const handleCommentSubmit = async (e) => {
    e.stopPropagation();
    if (!commentInput.trim() || !currentUserId || commentLoading) return;
    setCommentLoading(true);
    await supabase.from("match_reactions").insert({
      match_id: application.id, user_id: currentUserId, type: "comment", content: commentInput.trim(),
    });
    setCommentInput("");
    setCommentCount(c => c + 1);
    await loadComments();
    setCommentLoading(false);
  };

  /* ── 댓글 삭제 ── */
  const handleDeleteComment = async (e, commentId) => {
    e.stopPropagation();
    await supabase.from("match_reactions").delete().eq("id", commentId);
    setCommentCount(c => c - 1);
    await loadComments();
  };

  const isAccepted = application.status === "accepted";

  return (
    <div style={s.card}>
      {/* 카드 클릭 영역 */}
      <div onClick={() => onEventClick?.({ application, post, hostTeam, myTeam })}>
        {/* 카드 헤더: 상대팀 + 상태 배지 */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
          {thumbSrc ? (
            <img src={thumbSrc} alt="상대팀" style={s.thumb} />
          ) : (
            <div style={s.thumbFallback}>
              <span style={{ fontSize: 22 }}>{sport.icon}</span>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {hostTeam?.team_name || post?.team_name || "-"}
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{sport.label}</div>
          </div>
          <span style={{ ...s.badge, color: statusCfg.color, background: statusCfg.bg, border: `1px solid ${statusCfg.border}` }}>
            {statusCfg.label}
          </span>
        </div>

        {/* 날짜 · 장소 */}
        <div style={s.infoRow}>
          <span style={{ color: "#E8412A", fontWeight: 700 }}>📅</span>
          <span style={{ fontSize: 13, color: "#374151" }}>{formatGameDate(post?.game_date)}</span>
        </div>
        {buildLocation(post) !== "-" && (
          <div style={s.infoRow}>
            <span>📍</span>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{buildLocation(post)}</span>
          </div>
        )}

        {/* 우리 팀 */}
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, color: "#9ca3af" }}>우리 팀</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#1A1A2E" }}>{myTeam?.team_name || "-"}</span>
        </div>

        {/* 스코어 바 (result_status=confirmed인 경우) */}
        {result && (
          <div style={{ marginTop: 10 }}>
            <div style={{ padding: "10px 12px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: myWins ? "#E8412A" : "#374151", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {myTeam?.team_name || "우리팀"}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 20, fontWeight: 900, color: myWins ? "#E8412A" : isDraw ? "#1A1A2E" : "#d1d5db" }}>
                  {myScore}
                </span>
                <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 600 }}>:</span>
                <span style={{ fontSize: 20, fontWeight: 900, color: oppWins ? "#E8412A" : isDraw ? "#1A1A2E" : "#d1d5db" }}>
                  {oppScore}
                </span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: oppWins ? "#E8412A" : "#374151", flex: 1, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {hostTeam?.team_name || "상대팀"}
              </span>
            </div>
            <button
              onClick={e => { e.stopPropagation(); setShowCardImage(true); }}
              style={{ marginTop: 6, width: "100%", padding: "7px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}
            >
              <span>📷</span><span>결과 이미지 저장</span>
            </button>
          </div>
        )}
      </div>

      {/* 좋아요 / 댓글 버튼 (accepted 카드에만) */}
      {isAccepted && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #f3f4f6", display: "flex", gap: 8 }}>
          {/* 좋아요 */}
          <button
            onClick={handleLike}
            disabled={!currentUserId || likeLoading}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600,
              cursor: currentUserId ? "pointer" : "default",
              fontFamily: "inherit", border: "1px solid",
              background: liked ? "#fff0ee" : "#fff",
              color: liked ? "#E8412A" : "#6b7280",
              borderColor: liked ? "#E8412A" : "#d1d5db",
              opacity: likeLoading ? 0.6 : 1,
            }}
          >
            <span style={{ fontSize: 14 }}>{liked ? "❤️" : "🤍"}</span>
            <span>{likeCount}</span>
          </button>

          {/* 댓글 */}
          <button
            onClick={handleToggleComments}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
              background: showComments ? "#f0f4ff" : "#fff",
              color: showComments ? "#1A1A2E" : "#6b7280",
              border: `1px solid ${showComments ? "#1A1A2E" : "#d1d5db"}`,
            }}
          >
            <span style={{ fontSize: 14 }}>💬</span>
            <span>{commentCount}</span>
          </button>
        </div>
      )}

      {/* 이미지 저장 모달 */}
      {showCardImage && result && (
        <ScoreCardImage
          match={post}
          hostTeamName={isGuest ? (hostTeam?.team_name || "상대팀") : (myTeam?.team_name || "우리팀")}
          guestTeamName={isGuest ? (myTeam?.team_name || "우리팀") : (hostTeam?.team_name || "상대팀")}
          hostScore={result.host_score}
          guestScore={result.guest_score}
          detailScores={null}
          sportsType={post?.sports_type}
          onClose={() => setShowCardImage(false)}
        />
      )}

      {/* 댓글 섹션 */}
      {showComments && (
        <div style={{ marginTop: 10 }} onClick={e => e.stopPropagation()}>
          {/* 댓글 목록 */}
          {comments.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", margin: "0 0 10px", textAlign: "center" }}>
              아직 댓글이 없어요
            </p>
          ) : (
            <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {comments.map(c => (
                <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#1A1A2E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>{(c.nickname || "?")[0].toUpperCase()}</span>
                  </div>
                  <div style={{ flex: 1, background: "#f9fafb", borderRadius: 10, padding: "8px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1A1A2E" }}>{c.nickname}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(c.created_at)}</span>
                        {c.user_id === currentUserId && (
                          <button
                            onClick={e => handleDeleteComment(e, c.id)}
                            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 13, color: "#9ca3af", lineHeight: 1 }}
                          >🗑</button>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5, wordBreak: "break-all" }}>{c.content}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 댓글 입력창 */}
          {currentUserId ? (
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                placeholder="댓글을 입력하세요..."
                value={commentInput}
                onChange={e => setCommentInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleCommentSubmit(e); }}
                style={{
                  flex: 1, padding: "9px 12px", borderRadius: 10,
                  border: "1.5px solid #e5e7eb", fontSize: 13,
                  outline: "none", fontFamily: "inherit",
                }}
              />
              <button
                onClick={handleCommentSubmit}
                disabled={!commentInput.trim() || commentLoading}
                style={{
                  padding: "9px 14px", borderRadius: 10, border: "none",
                  background: "#E8412A", color: "#fff", fontSize: 13,
                  fontWeight: 700, cursor: commentInput.trim() ? "pointer" : "default",
                  opacity: commentLoading ? 0.6 : 1, fontFamily: "inherit",
                }}
              >
                {commentLoading ? "..." : "등록"}
              </button>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, textAlign: "center" }}>
              로그인 후 댓글을 달 수 있어요
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  container:   { minHeight: "100dvh", background: "#F5F5F7", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:      { padding: "calc(env(safe-area-inset-top) + 14px) 20px 14px", background: "#fff", borderBottom: "1px solid #edf0f3", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  backBtn:     { display: "flex", alignItems: "center", gap: 4, fontSize: 14, fontWeight: 600, color: "#E8412A", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" },
  headerTitle: { fontSize: 16, fontWeight: 700, color: "#111827" },
  body:        { flex: 1, padding: "16px 16px calc(80px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 12 },
  centered:    { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0" },
  spinner:     { width: 32, height: 32, border: "3px solid #edf0f3", borderTop: "3px solid #E8412A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },

  card:          { background: "#fff", borderRadius: 12, padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" },
  thumb:         { width: 48, height: 48, borderRadius: 10, objectFit: "cover", border: "1px solid #e5e7eb", flexShrink: 0 },
  thumbFallback: { width: 48, height: 48, borderRadius: 10, background: "#f5f6fa", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  badge:         { fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, flexShrink: 0 },
  infoRow:       { display: "flex", alignItems: "center", gap: 6, marginTop: 4 },
};
